import { Scope } from '@esg/esg-global-types';
import React from 'react';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  Checkbox,
  TextField
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { DocumentReference } from 'firebase/firestore';
import { DocumentReference as AdminDocumentReference } from 'firebase-admin/firestore';

export interface ActivityOwner {
  id: string;
  name: string;
  scope: Scope | DocumentReference | AdminDocumentReference | null;
  is_external: boolean;
}

interface ActivityOwnerSelectProps {
  handleChangeActivityOwner?: (
    activity_owners: Array<ActivityOwner> | ActivityOwner | null
  ) => void;
  options?: Array<ActivityOwner>;
  selected_options?: Array<ActivityOwner> | ActivityOwner | null;
  disabled?: boolean;
  input_label?: string;
  multi_select?: boolean;
  tag_limit?: number;
  clearable?: boolean;
  searchable?: boolean;
  group_id?: string;
  company_id?: string;
  loading?: boolean;
  color?: string;
  auto_select?: boolean;
  disable_selected_tags?: boolean;
  select_all?: boolean;
  variant?: 'standard' | 'outlined';
}

// Checkbox icons
const check_icon_blank: JSX.Element = <CheckBoxOutlineBlankIcon fontSize="small" />;
const check_icon_filled: JSX.Element = <CheckBoxIcon fontSize="small" />;

/**
 * Activity Owner selection auto-complete component that handles a variety of built-in scenarios.
 * @param {Array<ActivityOwner> | ActivityOwner | null} props.handleChangeActivityOwner Callback function to notify the parent component when the selected options have changed.
 * @param {Array<ActivityOwner>} props.options Custom list of options provided by the parent component. Defaults to none.
 * @param {Array<ActivityOwner> | ActivityOwner | null} props.selected_options Custom list of selected options provided by the parent component. Changes component from an uncontrolled to controlled.
 * @param {boolean} props.disabled Renders the component in a disabled state along with any other provided components. Defaults to enabled.
 * @param {string} props.input_label Sets a custom component input label. Defaults to the singular version of the main entity name.
 * @param {boolean} props.multi_select Allow multi option selection and keeps the select open while selecting. Defaults to a single select and closes after selecting an option.
 * @param {number} props.tag_limit Sets a limit on the amount of tags rendered in the input. Defaults to unlimited.
 * @param {boolean} props.clearable Allows the autocomplete to be cleared. Defaults to true.
 * @param {boolean} props.searchable Allows the user to type to search in the autocomplete. Defaults to true.
 * @param {boolean} props.loading A flag to set the autocomplete into a loading state for things such as fetching entries from database.
 * @param {boolean} props.auto_select Pre-populate the autocomplete input when only one division is available.
 * @param {string} props.group_id Group for which options will be queried.
 * @param {string} props.company_id Company for which options will be queried.
 * @param {boolean} props.select_all Optionally add a select all checkbox to the input component of the autocomplete when multiple selection is allowed.
 * @param {boolean} props.disable_selected_tags Optionally replace the selected tags with text when autocomplete is expanded to render input at a predictable height.
 * @param {string} props.variant An optional styling parameter for the textbox input of the autocomplete.
 * @returns {JSX.Element}
 */

const ActivityOwnerSelect = (props: ActivityOwnerSelectProps) => {
  const [optionList, setOptions] = React.useState<Array<ActivityOwner>>([]);
  const [selectedOptions, setSelectedOptions] = React.useState<
    Array<ActivityOwner> | ActivityOwner | null
  >(props.multi_select ? [] : null);

  // Define autocomplete options
  React.useEffect(() => {
    (async () => {
      const options: Array<ActivityOwner> = props.options
        ? // External Companies supplied by the parent component.
          props.options
        : [];
      setOptions(options);
      if (props.auto_select && options.length === 1 && options[0] !== selectedOptions) {
        setSelectedOptions(options[0]);
        props.handleChangeActivityOwner && props.handleChangeActivityOwner(options[0]);
      }
    })();
  }, [props.options]);

  // Define selected options
  React.useEffect(() => {
    setSelectedOptions(
      props.multi_select
        ? Array.isArray(props.selected_options)
          ? props.selected_options
          : []
        : props.selected_options
          ? props.selected_options
          : null
    );
  }, [props.options, props.selected_options]);

  const checkAll = (): boolean => {
    if (Array.isArray(selectedOptions)) {
      return optionList.length === selectedOptions.length;
    }
    return false;
  };

  const changeAll = (): void => {
    if (Array.isArray(selectedOptions)) {
      if (selectedOptions.length > 0) {
        setSelectedOptions([]);
        props.handleChangeActivityOwner && props.handleChangeActivityOwner([]);
      } else {
        setSelectedOptions(optionList);
        props.handleChangeActivityOwner && props.handleChangeActivityOwner(optionList);
      }
    }
  };

  return (
    <Autocomplete
      id="activity-owner-select"
      fullWidth
      disabled={props.disabled}
      loading={props.loading}
      limitTags={props.tag_limit}
      disableClearable={props.clearable === false}
      options={optionList}
      value={selectedOptions}
      getOptionLabel={(option: ActivityOwner) => {
        return option.name;
      }}
      renderOption={(render_props, option) => (
        <Box
          component="li"
          sx={{ display: 'flex', alignItems: 'center' }}
          {...render_props}
          key={option.id}
        >
          {option.name}
        </Box>
      )}
      disableCloseOnSelect={props.multi_select}
      isOptionEqualToValue={(option: ActivityOwner, value: ActivityOwner) =>
        option.id === value.id && option.id === value.id
      }
      multiple={props.multi_select}
      noOptionsText={`No Activity Owners`}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          label={props.input_label ?? 'Activity Owner'}
          variant={props.variant ?? 'standard'}
          slotProps={{
            htmlInput: { ...params.inputProps },
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {props.select_all && props.multi_select && (
                    <Box component="div">
                      <Box
                        component="li"
                        style={{
                          fontSize: '0.85rem',
                          color: '#616161',
                          fontWeight: 500
                        }}
                      >
                        <Checkbox
                          icon={check_icon_blank}
                          checkedIcon={check_icon_filled}
                          checked={checkAll()}
                          onChange={() => {
                            changeAll();
                          }}
                        />
                        Select All
                      </Box>
                    </Box>
                  )}
                  {params.InputProps.endAdornment}
                </>
              )
            }
          }}
        />
      )}
      onChange={(
        event: React.SyntheticEvent<Element, Event>,
        value: Array<ActivityOwner> | ActivityOwner | null
      ) => {
        setSelectedOptions(value);
        props.handleChangeActivityOwner && props.handleChangeActivityOwner(value);
      }}
    />
  );
};

export default ActivityOwnerSelect;
